import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Database, KeyRound, Plus, Table2, Terminal, Zap } from 'lucide-react';
import { api } from '../api/client';
import type { Connection, McpToken } from '../api/client';
import { Alert, EmptyState, Spinner } from '../components/Feedback';
import { CopyButton } from '../components/CopyButton';
import { useAuth } from '../components/AuthProvider';

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [connections, setConnections] = useState<Connection[] | null>(null);
  const [tokens, setTokens] = useState<McpToken[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.databases
      .list()
      .then(setConnections)
      .catch((e) => setError(e.message));
    api.mcp
      .tokens()
      .then(setTokens)
      .catch((e) => setError(e.message));
  }, []);

  const live = connections?.filter((c) => c.status === 'connected').length ?? 0;
  const activeTokens = (tokens ?? []).filter((t) => !t.revoked && !t.expired).length;

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <p className="eyebrow">OVERVIEW</p>
          <h1>Welcome back, {user?.name}</h1>
          <p className="muted">
            Connect a database, then generate an MCP token for your AI client to use.
          </p>
        </div>
        <Link className="primary" to="/databases/new">
          <Plus size={17} /> Add database
        </Link>
      </header>

      {error && <Alert tone="error">{error}</Alert>}

      <div className="stats">
        <Stat icon={Database} label="Databases" value={connections?.length ?? 0} />
        <Stat icon={Zap} label="Connected" value={live} />
        <Stat icon={KeyRound} label="Active MCP tokens" value={activeTokens} />
      </div>

      <section className="panel">
        <div className="panel-head">
          <h2>Getting started</h2>
        </div>
        <ol className="steps-list">
          <li>
            <strong>Register a database.</strong> MCPForge tests the connection, then stores the
            password encrypted. <Link to="/databases/new">Add a database</Link>
          </li>
          <li>
            <strong>Generate an MCP token.</strong> This is the credential your AI client
            presents to reach your data. <Link to="/mcp-tokens">Create a token</Link>
          </li>
          <li>
            <strong>Point your client at the MCP endpoint.</strong> Paste the server URL and
            your token into your MCP client configuration.
          </li>
        </ol>
      </section>

      <div className="grid-two">
        <section className="panel">
          <div className="panel-head">
            <h2>Databases</h2>
            <Link className="link" to="/databases">
              View all
            </Link>
          </div>
          {connections === null ? (
            <Spinner />
          ) : connections.length === 0 ? (
            <EmptyState
              title="No databases yet"
              description="Add your first database to let an agent query it."
            />
          ) : (
            <ul className="db-list">
              {connections.slice(0, 5).map((c) => (
                <li key={c.id}>
                  <div>
                    <strong>{c.name}</strong>
                    <span className="muted">
                      {c.database_type} · {c.database_name}
                    </span>
                  </div>
                  <StatusPill status={c.status} />
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="panel">
          <div className="panel-head">
            <h2>Quick links</h2>
          </div>
          <div className="quick-links">
            <Link to="/databases">
              <Database size={18} />
              <span>
                <strong>Manage databases</strong>
                <small>Add, test, and remove connections</small>
              </span>
            </Link>
            <Link to="/mcp-tokens">
              <KeyRound size={18} />
              <span>
                <strong>MCP tokens</strong>
                <small>Generate and revoke agent credentials</small>
              </span>
            </Link>
            <Link to="/databases">
              <Table2 size={18} />
              <span>
                <strong>Browse a schema</strong>
                <small>Inspect tables and columns</small>
              </span>
            </Link>
            <Link to="/query">
              <Terminal size={18} />
              <span>
                <strong>Query editor</strong>
                <small>Run read-only SQL yourself</small>
              </span>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Database;
  label: string;
  value: number;
}) {
  return (
    <div className="stat">
      <Icon size={20} />
      <div>
        <strong>{value}</strong>
        <span>{label}</span>
      </div>
    </div>
  );
}

export function StatusPill({ status }: { status: string }) {
  const labels: Record<string, string> = {
    connected: 'Connected',
    error: 'Unreachable',
    unknown: 'Untested',
  };
  return (
    <span className={`status status-${status}`}>
      <i />
      {labels[status] ?? status}
    </span>
  );
}
